import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy, 
  faUsers, 
  faHandshake, 
  faLeaf,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';

const ChiSiamo: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Chi Siamo</h1>
            <p className="text-xl text-white/80">
              Servizio Bolletta è un servizio indipendente di consulenza e comparazione 
              delle offerte per luce, gas e internet, gestito da GT Energie Srl
            </p>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-primary/5 border-b border-primary/10">
        <div className="container mx-auto py-4 px-4">
          <div className="flex items-center gap-3 justify-center text-center">
            <FontAwesomeIcon icon={faInfoCircle} className="text-primary flex-shrink-0" />
            <p className="text-sm text-light-muted">
              Non siamo il servizio clienti ufficiale di alcun fornitore di energia. 
              Offriamo un servizio gratuito e indipendente di confronto offerte.
            </p>
          </div>
        </div>
      </section>

      {/* Chi gestisce il servizio */}
      <section className="py-20">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2"
            >
              <h2 className="text-3xl font-bold mb-6 text-primary">Chi gestisce Servizio Bolletta</h2>
              <div className="space-y-4">
                <p className="text-light-muted leading-relaxed">
                  Servizio Bolletta è un marchio commerciale gestito da <strong className="text-light-text">GT Energie Srl</strong>, 
                  società con sede legale in Via Filippo Argelati 10, 20143 Milano (MI), P.IVA IT02812720809.
                </p>
                <p className="text-light-muted leading-relaxed">
                  Operiamo come <strong className="text-light-text">intermediario indipendente</strong> nel settore energetico e 
                  delle telecomunicazioni. Il nostro ruolo è confrontare le offerte disponibili sul mercato e fornire 
                  consulenza gratuita ai consumatori per aiutarli a trovare la soluzione più adatta alle loro esigenze.
                </p>
                <p className="text-light-muted leading-relaxed">
                  <strong className="text-light-text">Non siamo il servizio clienti ufficiale</strong> di alcun fornitore 
                  di energia, gas o servizi internet. Non rappresentiamo in modo esclusivo alcun operatore del settore.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2"
            >
              <div className="bg-light-surface p-8 rounded-lg shadow-md">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '10+', label: 'Anni di esperienza' },
                    { value: '15k+', label: 'Consulenze erogate' },
                    { value: '10+', label: 'Fornitori confrontati' },
                    { value: '100+', label: 'Consulenti dedicati' },
                  ].map((stat, i) => (
                    <motion.div 
                      key={stat.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="text-center p-4"
                    >
                      <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                      <p className="text-light-muted text-sm">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Il nostro servizio */}
      <section className="py-16 bg-light-background">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-8 text-primary text-center">Come funziona il nostro servizio</h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-light-border/30">
                <h3 className="text-lg font-semibold text-light-text mb-3">Il servizio è gratuito</h3>
                <p className="text-light-muted text-sm leading-relaxed">
                  La consulenza offerta da Servizio Bolletta è completamente gratuita per l'utente. 
                  Non applichiamo alcun costo, commissione o spesa a carico del consumatore.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-light-border/30">
                <h3 className="text-lg font-semibold text-light-text mb-3">Siamo indipendenti</h3>
                <p className="text-light-muted text-sm leading-relaxed">
                  Non siamo il servizio clienti ufficiale di alcun fornitore di energia. Operiamo in modo 
                  indipendente, confrontando le offerte di diversi operatori per presentarti le opzioni disponibili.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-light-border/30">
                <h3 className="text-lg font-semibold text-light-text mb-3">Nessun obbligo</h3>
                <p className="text-light-muted text-sm leading-relaxed">
                  L'eventuale attivazione di un'offerta è facoltativa e a totale discrezione del cliente. 
                  Puoi ricevere informazioni e confrontare le offerte senza alcun impegno di sottoscrizione.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-light-border/30">
                <h3 className="text-lg font-semibold text-light-text mb-3">Il nostro modello di business</h3>
                <p className="text-light-muted text-sm leading-relaxed">
                  Il servizio è gratuito per gli utenti. In alcuni casi, Servizio Bolletta potrebbe percepire 
                  una commissione commerciale dai fornitori partner qualora il cliente decida autonomamente 
                  di attivare un'offerta. Questo non influisce in alcun modo sul costo dell'offerta per il cliente.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* I Nostri Valori */}
      <section className="py-20">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">I Nostri Valori</h2>
            <p className="text-lg text-light-muted max-w-3xl mx-auto">
              Principi che guidano ogni nostra azione e decisione
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: faTrophy, title: 'Indipendenza', desc: 'Confrontiamo le offerte in modo imparziale, senza favorire alcun fornitore specifico.' },
              { icon: faUsers, title: 'Cliente al Centro', desc: 'Ascoltiamo le tue esigenze per offrirti un confronto su misura. La scelta è sempre tua.' },
              { icon: faHandshake, title: 'Trasparenza', desc: 'Nessun costo nascosto, nessun obbligo. Comunichiamo in modo chiaro e onesto.' },
              { icon: faLeaf, title: 'Sostenibilità', desc: 'Includiamo nel confronto anche offerte green e soluzioni energetiche sostenibili.' },
            ].map((valore, index) => (
              <motion.div 
                key={valore.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                className="card text-center"
              >
                <div className="inline-block p-4 rounded-full bg-primary/10 text-primary mb-6">
                  <FontAwesomeIcon icon={valore.icon} className="text-3xl" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-light-text">{valore.title}</h3>
                <p className="text-light-muted text-sm">{valore.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-12 px-4"
      >
        <div className="container mx-auto">
          <CallToAction 
            title="Vuoi confrontare le offerte?" 
            subtitle="Parla con un nostro consulente per una consulenza gratuita e senza impegno"
            variant="accent"
            className="mt-8"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default ChiSiamo;
